<script lang="ts">
    import { mdiTimer } from "@mdi/js"
    import { icons } from "../../../config"
    import { getRenderedComponent, tooltip } from "../../../helper"
    import { _ } from "svelte-i18n"
    import Icon from "../Icon.svelte"
    import DateTimeField from "./DateTimeField.svelte"
    import TrackingListField from "./TrackingListField.svelte"

    export let position: OperationPosition

    let delayedConfirmation = false
    let delayedManufacture = false
    let delayedReadyToShip = false

    let inObjectionPeriod = false
    let overObjectionPeriod = false

    const code = position.status?.code || 0
    if (code >= 40 && code <= 44) {
        const _end = new Date(position.objectionPeriodEnd).getTime()
        if (_end > Date.now()) {
            inObjectionPeriod = true
        } else {
            overObjectionPeriod = true
        }
    } else if (code > 44) {
        overObjectionPeriod = true
    }

    const planned = position.delivery?.plannedDate && new Date(position.delivery?.plannedDate).getTime()
    const now = Date.now()
    const status = position.status?.code || 0
    if (status <= 91 && planned && planned < now) {
        switch (true) {
            case status < 40:
                delayedConfirmation = true
                break
            case status < 90:
                delayedManufacture = true
                break
            default:
                delayedReadyToShip = true
        }
    }

    let confirmTooltip
    if (position.status?.code >= 40) {
        confirmTooltip = $_("components.table.fields.status.orderConfirmed")
    } else {
        confirmTooltip = $_("components.table.fields.status.orderInProgress")
    }

    let objectionTooltip: Tooltip = {
        content: $_("components.table.fields.status.objectionPeriod"),
    }
    async function renderObjecttionTooltip(inObjectionPeriod: boolean, overObjectionPeriod: boolean) {
        const date = (
            await getRenderedComponent(DateTimeField, {
                value: position.objectionPeriodEnd,
            })
        ).innerText
        if (inObjectionPeriod) {
            objectionTooltip.content = $_("components.table.fields.status.inObjectionPeriodUntil", {
                values: {
                    date: date,
                },
            })
        } else if (overObjectionPeriod) {
            const dateInfo = date ? $_("components.table.fields.status.dateOn", { values: { date: date } }) : ""
            objectionTooltip.content = $_("components.table.fields.status.objectionPeriodExpiredOn", {
                values: {
                    dateInfo: dateInfo,
                },
            })
        } else {
            objectionTooltip.content = $_("components.table.fields.status.objectionPeriodAfterConfirmation")
        }
    }
    $: renderObjecttionTooltip(inObjectionPeriod, overObjectionPeriod)

    let manufactureTooltip
    if (position.status?.code >= 45 && position.status.code < 90) {
        manufactureTooltip = $_("components.table.fields.status.positionInManufacture")
    } else if (position.status?.code >= 90) {
        manufactureTooltip = $_("components.table.fields.status.manufactureCompleted")
    } else {
        manufactureTooltip = $_("components.table.fields.status.manufactureNotStarted")
    }

    let readyToShipTooltip

    if (position.status?.code >= 90 && position.status.code <= 91) {
        readyToShipTooltip = $_("components.table.fields.status.positionReadyToShip")
    } else if (position.status?.code > 91) {
        readyToShipTooltip = $_("components.table.fields.status.readyToShipCompleted")
    } else {
        readyToShipTooltip = $_("components.table.fields.status.noPositionsInShipping")
    }
    let inShipmentTooltip
    let delivered = position.status?.code > 91
    position.tracking?.forEach((t) => {
        delivered = delivered && t.status >= 99
    })

    if (delivered) {
        if (position.tracking?.length) {
            inShipmentTooltip = $_("components.table.fields.status.positionDelivered")
        } else {
            inShipmentTooltip = $_("components.table.fields.status.positionShippedNoTracking")
        }
    } else if (position.status?.code > 91) {
        inShipmentTooltip = $_("components.table.fields.status.positionShipped")
    } else {
        inShipmentTooltip = $_("components.table.fields.status.positionNotShipped")
    }
    let deliveredTooltip: any = null
    if (position.status?.code > 40 && position.tracking.length === 1) {
        deliveredTooltip = {
            content: {
                component: TrackingListField,
                props: {
                    position,
                    doClose: null,
                    disabledLink: true,
                },
            },
            className: "tracking_overlay noclose",
            when: "hover",
        }
    } else {
        deliveredTooltip = { content: inShipmentTooltip }
    }
</script>

<div class="states">
    <div
        class="state {delayedConfirmation
            ? 'red'
            : position.status?.code
              ? position.status.code >= 40
                  ? 'green'
                  : 'yellow'
              : 'grey'}"
        use:tooltip="{{ content: confirmTooltip }}"
    >
        <span class="icon"><Icon path="{icons.confirmation}" size="20px" viewBox="0 0 20 20" /></span>
    </div>
    <div
        class="state"
        class:grey="{!overObjectionPeriod && !inObjectionPeriod}"
        class:green="{overObjectionPeriod}"
        class:yellow="{inObjectionPeriod}"
        use:tooltip="{objectionTooltip}"
    >
        <span class="icon"><Icon path="{mdiTimer}" /></span>
    </div>
    <div
        class="state {delayedManufacture
            ? 'red'
            : position.status?.code >= 45
              ? position.status.code >= 90
                  ? 'green'
                  : 'yellow'
              : 'grey'}"
        use:tooltip="{{ content: manufactureTooltip }}"
    >
        <span class="icon"><Icon path="{icons.manufacture}" size="18px" viewBox="0 0 18 18" /></span>
    </div>
    <div
        class="state {delayedReadyToShip
            ? 'red'
            : position.status?.code >= 90
              ? position.status.code > 91
                  ? 'green'
                  : 'yellow'
              : 'grey'}"
        use:tooltip="{{ content: readyToShipTooltip }}"
    >
        <span class="icon"><Icon path="{icons.readyToShip}" size="20px" viewBox="0 0 20 20" /></span>
    </div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
        class="state {position.status?.code > 91 ? (delivered ? 'green' : 'yellow') : 'grey'}"
        role="button"
        tabindex="0"
        on:click|stopPropagation
        use:tooltip="{deliveredTooltip}"
    >
        <span class="icon"><Icon path="{icons.shipping}" size="20px" viewBox="0 0 20 20" /></span>
        {#if delivered && !position.tracking?.length}
            <span class="redpoint"></span>
        {/if}
    </div>
</div>
